import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import Swal from 'sweetalert2';
import { urls } from '../../services/apiService';

export default function AssignDeviceDialog({ isOpen, onClose, onAssign, user }) {
  const [email, setEmail] = useState("");
  const [deviceId, setDeviceId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignedDevices, setAssignedDevices] = useState([]);
  const [showDevicesDropdown, setShowDevicesDropdown] = useState(false);

  useEffect(() => {
    if (user?.username) {
      setEmail(user.username);
      handleSearch(user.username);
    }
  }, [user]);

  const handleSearch = async (email) => {
    if (!email) {
      setError('Email is required');
      return;
    }

    setIsSearchLoading(true);
    setError(null);
    setAssignedDevices([]);

    try {
      const response = await fetch(urls.search_devices, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to search devices');
      }

      setAssignedDevices(data.devices || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!email || !deviceId) {
      setError('Both email and device ID are required');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(urls.assign_device, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, device_id: deviceId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to assign device');
      }
      
      if(response.ok){
        Swal.fire({
          title: "Device Assigned!",
          text: "Device assign successfully.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
        setDeviceId('');
        // Refresh the assigned devices list
        handleSearch(email);
      }

      onAssign(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setDeviceId('');
    setError(null);
    onClose();
  };

  const toggleDevicesDropdown = () => {
    setShowDevicesDropdown(!showDevicesDropdown);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30">
      <DialogPanel className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
        <DialogTitle className="text-2xl font-bold mb-6 text-gray-800">
          Assign New Device
        </DialogTitle>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              User Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="user@example.com"
              readOnly={!!user}
            />
          </div>

      

          <div>
            <label htmlFor="deviceId" className="block text-sm font-medium text-gray-700 mb-1">
              Device ID
            </label>
            <input
              id="deviceId"
              type="text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Device identifier"
            />
          </div>
        </div>


            {/* Assigned Devices Dropdown */}
            <div className="relative mt-3">
           
            <button
              onClick={toggleDevicesDropdown}
              className="w-full flex justify-between items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span>Assigned Devices ({assignedDevices.length})</span>
              <svg
                className={`w-5 h-5 transition-transform ${showDevicesDropdown ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showDevicesDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {assignedDevices.length > 0 ? (
                  <ul className="py-1">
                    {assignedDevices.map((device, index) => (
                      <li key={index} className="px-4 py-2 hover:bg-gray-100">
                        <div className="flex justify-between">
                          <span className="font-medium">Device ID:</span>
                          <span>{device}</span>
                        </div>
                        
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-2 text-gray-500">No devices assigned</div>
                )}
              </div>
            )}
          </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            className="px-5 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:bg-blue-400"
            disabled={isLoading}
          >
            {isLoading ? 'Assigning Device...' : 'Assign Device'}
          </button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}